import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../config/constants';

interface Purchase {
  phase_number: number;
  amount_usd: number;
  amount_crypto: number;
  crypto_symbol: string;
  tokens_allocated: number;
  tx_hash: string;
  status: string;
  created_at: string;
  completed_at: string;
}

interface UserTotals {
  total_usd: number;
  total_tokens: number;
  total_purchases: number;
}

interface UserPurchasesData {
  totals: UserTotals;
  purchases: Purchase[];
}

interface UserDashboardProps {
  walletAddress: string;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ walletAddress }) => {
  const [purchasesData, setPurchasesData] = useState<UserPurchasesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (walletAddress) {
      fetchUserPurchases();
    }
  }, [walletAddress]);

  const fetchUserPurchases = async () => {
    try {
      setError(null);
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/wallet/${walletAddress}/purchases`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user purchases');
      }
      
      const data = await response.json();
      setPurchasesData(data);
    } catch (err) {
      console.error('Error fetching user purchases:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch purchases');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUserPurchases();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (num: number, decimals: number = 2): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPhaseColor = (phase: number): string => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    return colors[(phase - 1) % colors.length] || '#6b7280';
  };

  const truncateHash = (hash: string): string => {
    if (!hash) return 'N/A';
    return `${hash.substring(0, 8)}...${hash.substring(hash.length - 8)}`;
  };

  if (loading) {
    return (
      <div className="user-dashboard loading">
        <div className="dashboard-header">
          <h3>📊 Your Dashboard</h3>
        </div>
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading your purchase history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-dashboard error">
        <div className="dashboard-header">
          <h3>📊 Your Dashboard</h3>
        </div>
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={fetchUserPurchases} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!purchasesData) {
    return null;
  }

  return (
    <div className="user-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h3>📊 Your Dashboard</h3>
          <p className="wallet-info">
            {walletAddress.substring(0, 6)}...{walletAddress.substring(walletAddress.length - 4)}
          </p>
        </div>
        <button 
          onClick={handleRefresh}
          className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
          disabled={isRefreshing}
        >
          {isRefreshing ? '⟳' : '🔄'} Refresh
        </button>
      </div>

      <div className="summary-cards">
        <div className="summary-card total-invested">
          <div className="card-header">
            <h4>Total Invested</h4>
            <span className="card-icon">💰</span>
          </div>
          <div className="card-value">
            {formatCurrency(purchasesData.totals.total_usd)}
          </div>
        </div>

        <div className="summary-card total-tokens">
          <div className="card-header">
            <h4>Tokens Purchased</h4>
            <span className="card-icon">🪙</span>
          </div>
          <div className="card-value">
            {formatNumber(purchasesData.totals.total_tokens, 2)}
          </div>
        </div>

        <div className="summary-card total-purchases">
          <div className="card-header">
            <h4>Total Purchases</h4>
            <span className="card-icon">📦</span>
          </div>
          <div className="card-value">
            {purchasesData.totals.total_purchases}
          </div>
        </div>
      </div>

      <div className="purchases-section">
        <div className="section-header">
          <h4>Purchase History</h4>
          {purchasesData.purchases.length > 0 && (
            <span className="purchase-count">
              {purchasesData.purchases.length} transaction{purchasesData.purchases.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {purchasesData.purchases.length === 0 ? (
          <div className="no-purchases">
            <div className="no-purchases-icon">📭</div>
            <h5>No Purchases Yet</h5>
            <p>Your completed purchases will appear here once you make your first payment.</p>
          </div>
        ) : (
          <div className="purchases-list">
            {purchasesData.purchases.map((purchase, index) => (
              <div key={index} className="purchase-card">
                <div className="purchase-header">
                  <div className="phase-badge" style={{ backgroundColor: getPhaseColor(purchase.phase_number) }}>
                    Phase {purchase.phase_number}
                  </div>
                  <div className="purchase-date">
                    {formatDate(purchase.completed_at || purchase.created_at)}
                  </div>
                </div>

                <div className="purchase-details">
                  <div className="detail-row">
                    <span className="detail-label">Amount Paid:</span>
                    <div className="detail-value">
                      <span className="usd-amount">{formatCurrency(purchase.amount_usd)}</span>
                      {purchase.amount_crypto && purchase.crypto_symbol && (
                        <span className="crypto-amount">
                          ({formatNumber(purchase.amount_crypto, 6)} {purchase.crypto_symbol})
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Tokens Received:</span>
                    <span className="detail-value token-amount">
                      {formatNumber(purchase.tokens_allocated, 2)}
                    </span>
                  </div>

                  {purchase.tx_hash && (
                    <div className="detail-row">
                      <span className="detail-label">Transaction:</span>
                      <div className="detail-value">
                        <span className="tx-hash" title={purchase.tx_hash}>
                          {truncateHash(purchase.tx_hash)}
                        </span>
                        <button 
                          onClick={() => navigator.clipboard.writeText(purchase.tx_hash)}
                          className="copy-hash-button"
                          title="Copy transaction hash"
                        >
                          📋
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge ${purchase.status}`}>
                      {['completed', 'completed_success'].includes(purchase.status) ? '✅' : '⏳'} {purchase.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {purchasesData.totals.total_purchases > 0 && (
        <div className="dashboard-footer">
          <div className="footer-stats">
            <div className="stat-item">
              <span className="stat-label">Average Purchase:</span>
              <span className="stat-value">
                {formatCurrency(purchasesData.totals.total_usd / purchasesData.totals.total_purchases)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Average Price:</span>
              <span className="stat-value">
                {formatCurrency(purchasesData.totals.total_usd / purchasesData.totals.total_tokens)} per token
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
